import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    Grid,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link as RouterLink } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import PlanUsagePanel from '../../components/subscription/PlanUsagePanel';
import { formatCurrencyInCents } from '../../helpers/formatters';
import {
    DowngradeConfirmation,
    startSubscriptionCheckoutAfterMigrate,
    UpgradeConfirmation,
    patchAccountFromResponse,
    planMonthlyPrice,
    SCROLL_DURATION_MS,
    smoothScrollToElement,
    SubscriptionPlanCard,
} from '../../components/subscription/subscriptionPlanMigration';
import {
    isAppInboxLiveNotification,
    LIVE_INBOX_KINDS,
    subscribeLiveNotifications,
} from '../../helpers/liveNotifications';
import {
    canonicalPlanName,
    comparePlanChange,
    formatPlanRenewalDate,
    isSameSubscriptionPlan,
    planTierRank,
} from '../../helpers/subscription';

const PENDING_MP_DISCLAIMER = `Sua migração foi registrada. Conclua a assinatura no Mercado Pago para ativar o novo plano.

Nos planos Crescendo e Profissional você tem 7 dias grátis. No Ilimitado a cobrança é imediata.`;

const PAYMENT_STATUS_LABELS = {
    pending_validation: 'Aguardando validação',
    approved: 'Pago',
    rejected: 'Recusado',
    pending: 'Pendente',
    refunded: 'Estornado',
};

function formatDateTime(value) {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '—';
    }
    return date.toLocaleString('pt-BR');
}

function formatPaymentDate(payment) {
    if (payment?.status === 'approved') {
        return formatDateTime(payment.paid_at || payment.reviewed_at);
    }
    if (payment?.status === 'pending_validation' || payment?.status === 'pending') {
        return 'Pendente';
    }
    if (payment?.status === 'rejected') {
        return 'Recusado';
    }
    return '—';
}

function MySubscriptionPage() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [subscription, setSubscription] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedChangeType, setSelectedChangeType] = useState(null);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const termsSectionRef = useRef(null);
    const scrollAnimationRef = useRef(null);
    const pendingPaymentRef = useRef(null);

    const loadSubscription = useCallback(() => (
        api.get('/me/subscription')
            .then((response) => {
                const data = response?.data?.data || null;
                setSubscription(data);
                if (data?.plan?.name) {
                    patchAccountFromResponse(data);
                }
                return data;
            })
            .catch((err) => {
                setError(err?.response?.data?.error || 'Não foi possível carregar sua assinatura.');
                throw err;
            })
    ), []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        loadSubscription()
            .catch(() => {})
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [loadSubscription]);

    useEffect(() => () => {
        if (scrollAnimationRef.current) {
            cancelAnimationFrame(scrollAnimationRef.current);
        }
    }, []);

    useEffect(() => subscribeLiveNotifications((notification) => {
        if (!isAppInboxLiveNotification(notification)) {
            return;
        }
        if (LIVE_INBOX_KINDS.includes(notification.kind)) {
            loadSubscription().catch(() => {});
        }
    }), [loadSubscription]);

    const currentPlan = subscription?.plan;
    const pendingPlan = subscription?.pending_plan;
    const pendingDowngradePlan = subscription?.pending_downgrade_plan;
    const planMigrationPending = Boolean(subscription?.plan_migration_pending);
    const planDowngradePending = Boolean(subscription?.plan_downgrade_pending);
    const renewalDateLabel = formatPlanRenewalDate(subscription?.plan_renewal_date);
    const plans = Array.isArray(subscription?.plans) ? subscription.plans : [];
    const payments = Array.isArray(subscription?.payments) ? subscription.payments : [];
    const currentPlanName = currentPlan?.name || 'Free';
    const graceUntil = subscription?.grace_until;
    const trialActive = Boolean(subscription?.trial_active);

    const sortedPlans = useMemo(
        () => [...plans].sort(
            (a, b) => planTierRank(a.name, planMonthlyPrice(a)) - planTierRank(b.name, planMonthlyPrice(b)),
        ),
        [plans],
    );

    const planColumnWidth = sortedPlans.length >= 4 ? 3 : 4;

    const isMigratingPlan = useCallback((plan) => {
        if (!planMigrationPending || !pendingPlan) {
            return false;
        }
        if (pendingPlan.uuid && plan.uuid) {
            return pendingPlan.uuid === plan.uuid;
        }
        return isSameSubscriptionPlan(pendingPlan.name, plan.name);
    }, [planMigrationPending, pendingPlan]);

    const isPendingDowngradePlan = useCallback((plan) => {
        if (!planDowngradePending || !pendingDowngradePlan) {
            return false;
        }
        if (pendingDowngradePlan.uuid && plan.uuid) {
            return pendingDowngradePlan.uuid === plan.uuid;
        }
        return isSameSubscriptionPlan(pendingDowngradePlan.name, plan.name);
    }, [planDowngradePending, pendingDowngradePlan]);

    const isPlanSelected = (plan) => {
        if (!selectedPlan) {
            return false;
        }
        if (selectedPlan.uuid && plan.uuid) {
            return selectedPlan.uuid === plan.uuid;
        }
        return selectedPlan.name === plan.name;
    };

    const handleSelectPlan = (plan) => {
        const direction = comparePlanChange(
            currentPlan?.name,
            planMonthlyPrice(currentPlan),
            plan.name,
            planMonthlyPrice(plan),
        );
        flushSync(() => {
            setSelectedPlan(plan);
            setSelectedChangeType(direction);
        });
        smoothScrollToElement(termsSectionRef.current, SCROLL_DURATION_MS, scrollAnimationRef);
    };

    const resetSelection = () => {
        setSelectedPlan(null);
        setSelectedChangeType(null);
    };

    const handleUpgradeSuccess = async (data) => {
        resetSelection();
        if (data) {
            setSubscription((prev) => ({ ...(prev || {}), ...data }));
            patchAccountFromResponse(data);
        } else {
            await loadSubscription();
        }
    };

    const handleDowngradeSuccess = async (data) => {
        resetSelection();
        if (data) {
            setSubscription((prev) => ({ ...(prev || {}), ...data }));
            patchAccountFromResponse(data);
        } else {
            await loadSubscription();
        }
    };

    const handleResumeCheckout = async () => {
        setCheckoutLoading(true);
        try {
            await startSubscriptionCheckoutAfterMigrate(null, () => loadSubscription());
        } finally {
            setCheckoutLoading(false);
        }
    };

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} to="/admin/my-account" underline="hover" color="inherit">
                    Minha conta
                </Link>
                <Typography color="text.primary">Minha assinatura</Typography>
            </Breadcrumbs>

            <Typography variant="h4" sx={{ mb: 1 }}>
                Minha assinatura
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
                Confira seu plano atual, migre ou regreda de plano, e acompanhe pagamentos pelo Mercado Pago.
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : null}

            {!loading && error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}

            {!loading && !error ? (
                <>
                    {subscription ? (
                        <Alert severity="info" sx={{ mb: 3, maxWidth: 720 }}>
                            Plano ativo: <strong>{canonicalPlanName(currentPlanName)}</strong>
                            {trialActive ? (
                                <>
                                    {' '}· <strong>Período de teste ativo</strong>
                                </>
                            ) : null}
                            {renewalDateLabel ? (
                                <>
                                    {' '}· Próxima cobrança em <strong>{renewalDateLabel}</strong>
                                </>
                            ) : null}
                            {planMigrationPending && pendingPlan?.name ? (
                                <>
                                    {' '}· Migração pendente para <strong>{pendingPlan.name}</strong>
                                </>
                            ) : null}
                            {planDowngradePending && pendingDowngradePlan?.name ? (
                                <>
                                    {' '}· Regressão agendada para <strong>{pendingDowngradePlan.name}</strong>
                                    {renewalDateLabel ? (
                                        <>
                                            {' '}em <strong>{renewalDateLabel}</strong>
                                        </>
                                    ) : null}
                                </>
                            ) : null}
                        </Alert>
                    ) : null}

                    {graceUntil ? (
                        <Alert severity="warning" sx={{ mb: 3, maxWidth: 720 }}>
                            Pagamento em atraso. Regularize até <strong>{formatDateTime(graceUntil)}</strong> para evitar suspensão do plano.
                        </Alert>
                    ) : null}

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {sortedPlans.map((plan) => {
                            const changeDirection = comparePlanChange(
                                currentPlan?.name,
                                planMonthlyPrice(currentPlan),
                                plan.name,
                                planMonthlyPrice(plan),
                            );
                            return (
                                <Grid item xs={12} sm={6} md={planColumnWidth} key={plan.uuid || plan.name}>
                                    <SubscriptionPlanCard
                                        plan={plan}
                                        isCurrent={isSameSubscriptionPlan(currentPlan?.name, plan.name)}
                                        isSelected={isPlanSelected(plan)}
                                        isMigrating={isMigratingPlan(plan)}
                                        isPendingDowngrade={isPendingDowngradePlan(plan)}
                                        changeDirection={changeDirection}
                                        onSelect={handleSelectPlan}
                                        busy={false}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>

                    <Box ref={termsSectionRef} sx={{ scrollMarginTop: 24 }}>
                        {selectedPlan && selectedChangeType === 'upgrade' ? (
                            <UpgradeConfirmation
                                key={`upgrade-${selectedPlan.uuid || selectedPlan.name}`}
                                plan={selectedPlan}
                                onBack={resetSelection}
                                onSuccess={handleUpgradeSuccess}
                            />
                        ) : null}
                        {selectedPlan && selectedChangeType === 'downgrade' ? (
                            <DowngradeConfirmation
                                key={`downgrade-${selectedPlan.uuid || selectedPlan.name}`}
                                plan={selectedPlan}
                                currentPlanName={canonicalPlanName(currentPlanName)}
                                renewalDateLabel={renewalDateLabel}
                                onBack={resetSelection}
                                onSuccess={handleDowngradeSuccess}
                            />
                        ) : null}
                    </Box>

                    <PlanUsagePanel usage={subscription?.usage} />

                    {planMigrationPending ? (
                        <Paper ref={pendingPaymentRef} variant="outlined" sx={{ p: 3, mb: 3, scrollMarginTop: 24 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Finalizar assinatura — {pendingPlan?.name || 'novo plano'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, whiteSpace: 'pre-line', maxWidth: 720 }}>
                                {PENDING_MP_DISCLAIMER}
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleResumeCheckout}
                                disabled={checkoutLoading}
                            >
                                {checkoutLoading ? 'Abrindo checkout…' : 'Continuar no Mercado Pago'}
                            </Button>
                        </Paper>
                    ) : null}

                    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Histórico de pagamentos
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Cobranças registradas automaticamente pelo Mercado Pago.
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Plano</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Valor</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Cobrança gerada</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Pagamento</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {payments.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                Nenhum pagamento registrado ainda.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        payments.map((payment) => (
                                            <TableRow key={payment.uuid}>
                                                <TableCell>
                                                    {canonicalPlanName(payment.subscription_plan_name || '—')}
                                                </TableCell>
                                                <TableCell>
                                                    {formatCurrencyInCents(Number(payment.amount_cents) || 0)}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDateTime(payment.charge_generated_at || payment.created_at)}
                                                </TableCell>
                                                <TableCell>
                                                    {formatPaymentDate(payment)}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={PAYMENT_STATUS_LABELS[payment.status] || payment.status || '—'}
                                                        color={
                                                            payment.status === 'approved'
                                                                ? 'success'
                                                                : payment.status === 'rejected'
                                                                    ? 'error'
                                                                    : 'warning'
                                                        }
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    <Button variant="outlined" component={RouterLink} to="/admin/my-account">
                        Voltar para Minha conta
                    </Button>
                </>
            ) : null}
        </Box>
    );
}

export default MySubscriptionPage;
